import moment from "moment";
import classNames from "classnames";
import { ROUTES } from "../../../routes";
import { useState, useEffect, useContext, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { FaArrowUp, FaArrowDown, FaRegQuestionCircle } from "react-icons/fa";
import AppUser from "../../../modules/app-user/app-user";
import Tooltip from "../../../components/common/Tooltip";
import ARIAChat from "../../../components/portal/ARIAChat";
import ApiSupervision from "../../../modules/api/supervision";
import FGDropdownApi from "../../../components/form/FGDropdownApi";
import { SUBMISSION_PD, SUBMISSION_WL } from "../../../config/constants";
import AppUserContext from "../../../context/app-user-context";
import ROLE from "../../../modules/role";
import ApiARIAAnalyze from "../../../modules/api/aria-analyze";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import useViewport from "../../../hooks/use-viewport";
import ApiMessage from "../../../modules/api/message";
import ModulePopup from "../../../modules/popup/popup";
import { showLabelInfo } from "../../../config/methods";
import usePageTitle from "../../../hooks/use-page-title";
import NewLineToBreak from "../../../components/static/NewLineToBreak";
import Config from "../../../config/config";
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight  } from "react-icons/fi";

const ENABLE_CHAT_DRAG_MODE = true;
const PAGE_TITLE = "Supervisor’s Dashboard";
const { ALLOW_INDUSTRY_SUPERVISOR_TO_VIEW_WL_REPORT } = Config;

const reportTypeOptions = [
  {
    id: "wl",
    name: SUBMISSION_WL,
    label: "Workplace Learning Reports",
    tooltip: "",
  },
  {
    id: "pd",
    name: SUBMISSION_PD,
    label: "Professional Development Reports",
    tooltip: "",
  },
];

interface IOptionItem {
  label: string;
  value: string;
  pdMode?: boolean;
}

interface PageDashboardSITProps {
  user: any;
}

type SortField = 'student' | 'programme' | 'cohort' | null;
type SortDirection = 'asc' | 'desc' | null;

const LABEL_INSTRUCTION_UNSELECTED = "Please select a Programme and a Cohort from above";
const LABEL_STUDENT_DATA_NOT_FOUND = "No student data found for selected Programme and Cohort";

export default function PageDashboardSIT(props: PageDashboardSITProps) {
  usePageTitle(PAGE_TITLE);
  usePortalSettingSet("label_header", PAGE_TITLE);

  const viewport = useViewport();
  const location = useLocation();
  const user = useContext(AppUserContext);
  const { setCustomText } = useContext(PortalContext);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [programmeId, setProgrammeId] = useState(``);
  const [programmeOptions, setProgrammeOptions] = useState([{ value: "0", label: "Select Programme..." }]);
  const [cohortOptions, setCohortOptions] = useState([{ value: "0", label: "Select Cohort..." }]);
  const [cohortId, setCohortId] = useState("");
  const [cohortIdForAria, setCohortIdForAria] = useState("");
  const [sort, setSort] = useState<{field: SortField, direction: SortDirection}>({field: "student", direction: "asc"});
  const [cohortPdMode, setCohortPdMode] = useState(true);
  const [reportType, setReportType] = useState("");
  const [reportTypes, setReportTypes] = useState(reportTypeOptions);
  const [isAriaChatVisible, setIsAriaChatVisible] = useState(false);
  const [askAriaLoadingIndex, setAskAriaLoadingIndex] = useState<number | null>(null);
  const [customDesc, setCustomDesc] = useState<string>("");
  const [ariaSuggestions, setAriaSuggestions] = useState<any[]>([]);
  const [askAriaStudentReportsFeedbacks, setAskAriaStudentReportsFeedbacks] = useState<any[]>([]);
  const [currentStudentReports, setCurrentStudentReports] = useState([]);
  const [cohortGroupByProgramme, setCohortGroupByProgramme] = useState({});
  const [overviewData, setOverviewData] = useState([]);
  const [studentRows, setStudentRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [alertText, setAlertText] = useState<string>(LABEL_INSTRUCTION_UNSELECTED);
  const [studentAccountId, setStudentAccountId] = useState<string>("");
  const [tooltip, setTooltip] = useState({
    content: null,
    position: null,
    closeButton: null,
  });

  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedOptions = useRef(false);
  const hasAppliedSavedFilters = useRef(false);
  const hasLoadedInitialData = useRef(false);
  const shouldFetchData = useRef(false);

  const [ariaChatPos, setAriaChatPos] = useState({ x: -9999, y: -9999 });
  const [isDragging, setIsDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });
  const ariaChatRef = useRef<HTMLDivElement>(null);

  const isDesktop = viewport && !viewport.isMobile;

  useEffect(() => {
    setCustomText(null);
  }, []);

  useEffect(() => {
    AppUser.refreshUser(["avatar"]);
  }, []);

  useEffect(() => {
    if (!user) return;
    setReportType(user.role === ROLE.INDUSTRY_SUPERVISOR && !ALLOW_INDUSTRY_SUPERVISOR_TO_VIEW_WL_REPORT
      ? SUBMISSION_PD
      : SUBMISSION_WL
    );
  }, [user]);

  useEffect(() => {
    if (props.user?.groupHash) {
      const cohortGroupByProgramme = getCohortGroupByProgramme();
      setCohortGroupByProgramme(cohortGroupByProgramme);
    }
  }, [props.user]);

  useEffect(() => {
    if (Object.keys(cohortGroupByProgramme).length > 0 && !hasInitializedOptions.current) {
      const options: IOptionItem[] = getProgrammeOptions(cohortGroupByProgramme);
      setProgrammeOptions([{ value: "0", label: "Select Programme..." }, ...options]);
      hasInitializedOptions.current = true;

      if (!hasAppliedSavedFilters.current) {
        loadSavedFilters();
      }
    }
  }, [cohortGroupByProgramme]);

  useEffect(() => {
      const cohortOpts: IOptionItem[] = getCohortOptions(programmeId) || [];
      setCohortOptions([{ value: "0", label: "Select Cohort..." }, ...cohortOpts]);

      if (hasAppliedSavedFilters.current && !hasLoadedInitialData.current) {
        const userId = user?.id;
        if (userId) {
          const localStorageKey = `CohortFilter_${userId}`;
          const savedFilter = localStorage.getItem(localStorageKey);
          try {
            const { cohort } = JSON.parse(savedFilter ?? "{}");
            if (cohort && cohortOpts.some(opt => opt.value === cohort)) {
              setCohortId(cohort);
            }
            shouldFetchData.current = true;
          } catch (e) {
            console.error("Error parsing saved filter:", e);
          }
        }
      }
  }, [programmeId]);

  // this is initial auto request if selected Programme and Cohort are cached
  useEffect(() => {
    if (
      shouldFetchData.current &&
      !hasLoadedInitialData.current
    ) {
      shouldFetchData.current = false;
      hasLoadedInitialData.current = true;

      setTimeout(() => {
        handleSubmitChangeProgrammeCohort();
      }, 100);
    }
  }, [shouldFetchData.current]);

  useEffect(() => {
    if (!Array.isArray(overviewData) || overviewData.length === 0) {
      setLoading(false);
      return;
    }

    fetchSentiments();
  }, [overviewData]);

  useEffect(() => {
    if (hasLoadedInitialData.current) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        handleSubmitChangeProgrammeCohort();
      }
    }
  }, [reportType]);

  // Trigger data refresh when pagination parameters change
  useEffect(() => {
    if (hasLoadedInitialData.current) {
      handleSubmitChangeProgrammeCohort();
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    setReportTypes(reportTypeOptions.filter((r) => {
      if (user.role === ROLE.INDUSTRY_SUPERVISOR && r.id !== "pd" && !ALLOW_INDUSTRY_SUPERVISOR_TO_VIEW_WL_REPORT) {
        return false;
      }
      if (!cohortPdMode && r.id !== "wl") {
        return false;
      }
      return true;
    }));
  }, [cohortPdMode, user]);

  function startAriaDrag(e) {
    if (!isDesktop || !ENABLE_CHAT_DRAG_MODE) return;
    setIsDragging(true);
    offset.current = {
      x: e.clientX - ariaChatPos.x,
      y: e.clientY - ariaChatPos.y,
    };
  }

  function onAriaDrag(e) {
    if (!isDragging || !ENABLE_CHAT_DRAG_MODE) return;

    const rect = ariaChatRef.current?.getBoundingClientRect();
    const chatWidth = rect?.width || 400;
    const chatHeight = rect?.height || 500;
    const padding = 20;

    let newX = e.clientX - offset.current.x;
    let newY = e.clientY - offset.current.y;

    newX = Math.max(padding, Math.min(window.innerWidth - chatWidth - padding, newX));
    newY = Math.max(padding, Math.min(window.innerHeight - chatHeight - padding, newY));

    setAriaChatPos({ x: newX, y: newY });
  }

  function stopAriaDrag() {
    setIsDragging(false);
  }

  useEffect(() => {
    if (!isDesktop) return;
    if (isDragging) {
      window.addEventListener('mousemove', onAriaDrag);
      window.addEventListener('mouseup', stopAriaDrag);
    }
    return () => {
      window.removeEventListener('mousemove', onAriaDrag);
      window.removeEventListener('mouseup', stopAriaDrag);
    };
  }, [isDragging, isDesktop]);

  const loadSavedFilters = () => {
    if (hasAppliedSavedFilters.current) return;

    const userId = user?.id;
    if (!userId) return;

    const localStorageKey = `CohortFilter_${userId}`;
    const savedFilter = localStorage.getItem(localStorageKey);

    try {
      const { programme = null } = JSON.parse(savedFilter ?? "{}");
      setProgrammeId(programme);
    } catch (e) {
      console.error("Error parsing saved filter:", e);
    }
    hasAppliedSavedFilters.current = true;

    // Load sorting settings from per-user cache (no URL param required)
    const sortStorageKey = `OverviewSort_${userId}`;
    const savedSort = localStorage.getItem(sortStorageKey);
    if (savedSort) setSort(JSON.parse(savedSort));
  };

  // Toggle helpers: cycle null -> 'asc' -> 'desc' -> null
  const cycleSort = (field: SortField, direction: SortDirection) => {
    if (direction === "desc") {
      return { field, direction: "asc" };
    } else {
      return { field, direction: "desc" }
    }

    // if (field !== sort.field) {
    //   return { field, direction: "asc" };
    // }
    // if (direction === "asc") {
    //   return { field, direction: "desc" }
    // }
    // return { field: null, direction: null };
  };

  const toggleSort = (field: "student" | "programme" | "cohort") => {
    if (loading) return;
    const sortNext = cycleSort(field, sort.direction);
    handleSubmitChangeProgrammeCohort(sortNext as { field: SortField, direction: SortDirection });
  }

  const handleSubmitChangeProgrammeCohort = (overrideSort?: { field: SortField, direction: SortDirection }) => {
    const userId = user?.id;
    if (!userId || loading) return;

    if (programmeId && cohortId) {
      const localStorageKey = `CohortFilter_${userId}`;
      localStorage.setItem(
        localStorageKey,
        JSON.stringify({ programme: programmeId, cohort: cohortId })
      );
    }

    // persist overview sort settings per user (use overrides when provided)
    const sortStorageKey = `OverviewSort_${userId}`;
    const sorts = overrideSort || sort;
    localStorage.setItem(sortStorageKey, JSON.stringify(sorts));
    // sync state if overrides provided
    overrideSort && setSort(overrideSort);

    setLoading(true);
    setAlertText("");
    setCohortPdMode(true);
    setOverviewData([]);

    let parsedReportType = reportType;
    const groupHash = props.user?.groupHash ?? {};
    // case 1: no programme & cohort selected
    let groupIds = Object.keys(groupHash);

    // case 2: programme selected but cohort not selected
    if (programmeId && programmeId !== "0" && (!cohortId || cohortId === "0")) {
      groupIds = groupIds.filter(gid => {
        const grp = groupHash[gid];
        return grp?.cohort?.programme?._id === programmeId;
      });
    }

    // case 3: programme and cohort selected
    if (programmeId && programmeId !== "0" && cohortId && cohortId !== "0") {
      groupIds = groupIds.filter(gid => {
        const grp = groupHash[gid];
        return grp?.cohort?._id === cohortId;
      });
      let matched = groupIds.map(gid => groupHash[gid])[0];
      if (matched) {
        setCohortPdMode(matched.cohort.pdMode);
        if (!matched.cohort.pdMode) {
          if (user.role === ROLE.INDUSTRY_SUPERVISOR && !ALLOW_INDUSTRY_SUPERVISOR_TO_VIEW_WL_REPORT) {
            parsedReportType = "";
          } else {
            parsedReportType = SUBMISSION_WL;
          }
        }
      }
    }

    // if user is industry supervisor, remove groups that belong to cohorts not in pdMode
    if (user?.role === ROLE.INDUSTRY_SUPERVISOR && !ALLOW_INDUSTRY_SUPERVISOR_TO_VIEW_WL_REPORT) {
      groupIds = groupIds.filter(gid => {
        const grp = groupHash[gid];
        return grp?.cohort?.pdMode;
      });
    }

    if (!groupIds || groupIds.length === 0 || !parsedReportType) {
      setAlertText(LABEL_STUDENT_DATA_NOT_FOUND);
      setLoading(false);
      return;
    } else {
      setReportType(parsedReportType);
    }

    ApiSupervision.getOverview(parsedReportType, groupIds.join(','), sorts, currentPage, itemsPerPage)
      .then((response) => {
        const data = response.data || [];
        const totalCount = response.totalCount || 0;
        setOverviewData(data);
        setTotalCount(totalCount);
        setCohortIdForAria(data[0]?.cohortId);
        // If programmeId/cohortId are not set but we have data, set them based on first row
        // if ((!programmeId || programmeId === "0") && data.length > 0) {
        //   const firstRow = data[0];
        //   // Find matching group from groupHash
        //   const matchingGroupId = Object.keys(groupHash).find(gid => {
        //     const grp = groupHash[gid];
        //     return grp?.cohort?.name === firstRow.cohortName &&
        //           grp?.cohort?.programme?.name === firstRow.programmeName;
        //   });

        //   if (matchingGroupId) {
        //     const grp = groupHash[matchingGroupId];
        //     setProgrammeId(grp?.cohort?.programme?._id || "0");
        //     setCohortId(grp?.cohort?._id || "0");
        //   }
        // }

        if (!data || (Array.isArray(data) && data.length === 0)) setAlertText(LABEL_STUDENT_DATA_NOT_FOUND); else setAlertText("");
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setAlertText("Error loading student data. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  const fetchSentiments = async () => {
    let rows = [...overviewData];

    rows.forEach(row => {
      row.image = "/assets/images/dummy/profile-picture/male.png";
      row.reports = composeReports(row.reports);
      row.sentiment = new Array(row.reports.length).fill("empty");
    });

    const studentIds = rows.map(row => row.accountId).filter(id => id);

    if (studentIds.length === 0) {
      setStudentRows(rows);
      setLoading(false);
      return;
    }

    try {
      const sentimentResponse = await ApiARIAAnalyze.supervisorDashboardReportOverview(studentIds);

      const sentimentMap = new Map();

      (sentimentResponse.data || []).forEach(item => {
        if (item.submissionId && item.studentId) {
          sentimentMap.set(
            `${item.studentId}-${item.submissionId}`,
            item.result?.content?.sentiment_analysis?.sentiment || "No Data"
          );
        }
      });

      rows.forEach(row => {
        row.reports.forEach((report, index) => {
          if (report.submittedDate) {
            if (report._id && row.accountId) {
              const key = `${row.accountId}-${report._id}`;
              if (sentimentMap.has(key)) {
                const sentimentValue = sentimentMap.get(key);
                if (sentimentValue === "Positive" || sentimentValue === "Negative" || sentimentValue === "Neutral") {
                  row.sentiment[index] = sentimentValue;
                } else {
                  row.sentiment[index] = "pending";
                }
              } else {
                row.sentiment[index] = "pending";
              }
            }
          } else {
            if (report.statusDate === "past") {
              row.sentiment[index] = "pending";
            } else if (report.statusDate === "future") {
              row.sentiment[index] = "empty";
            } else {
              row.sentiment[index] = "pending";
            }
          }
        });
      });

      setStudentRows(rows);
    } catch (error) {
      console.error("Error fetching sentiment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const composeReports = (reports: any[]) => {
    return reports.map((report) => {
      let from = new Date(report.from);
      let to = new Date(report.to);
      let submission = typeof report.submission === "object" ? report.submission : null;

      report._id = submission ? submission._id : null;
      report.deadline = to;

      // Fix: If there's a submission, prioritize that over date logic
      if (submission) {
        report.statusDate = currentDate > to ? "past" : "present";
      } else {
        report.statusDate = currentDate > to ? "past" : currentDate < from ? "future" : "present";
      }

      report.submittedDate = submission ? new Date(submission.createdDate) : null;
      report.feedbackDate = report.feedback && typeof report.feedback === "object"
        ? new Date(report.feedback.createdDate) : null;
      report.status = getReportStatus(report);

      return report;
    });
  };

  const handleMouseEnter = (e: any, content: any, closeButton = true) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = e.target.getBoundingClientRect();
    setTooltip({
      content,
      position: {
        top: rect.top + window.scrollY - 10,
        left: rect.left + window.scrollX + rect.width / 2,
      },
      closeButton,
    });
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip({ content: null, position: null, closeButton: null });
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    setTooltip({ content: null, position: null, closeButton: null });
  };

  const askAriaStudentFeedback = async (student: any) => {
    try {
      const submissionIds = student.reports
        .filter((report: any) => report._id)
        .map((report: any) => report._id)
        .join(',');

      if (submissionIds) {
        const response = await ApiMessage.getMessagesBySubmission(submissionIds);

        const filteredFeedback = response.messages.filter((feedback: any) => feedback.status !== "Draft");

        setAskAriaStudentReportsFeedbacks(filteredFeedback);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  const getReportsForStudent = (student) => {
    return student.reports
      .map((report, idx) => ({
        submissionId: report._id,
        order: idx + 1, // Assuming the order is correct from the backend
      }))
      .filter(report => !!report.submissionId)
  };

  const handleARIAClick = (index: number, student: any) => async () => {
    if (isAriaChatVisible && studentAccountId === student.accountId) return;
    setCohortIdForAria(student.cohortId);

    setAskAriaLoadingIndex(index);
    setIsAriaChatVisible(false);
    setCurrentStudentReports(getReportsForStudent(student));

    const fetchPromise = askAriaStudentFeedback(student);
    const timeoutPromise = new Promise(res => setTimeout(res, 1500));
    await Promise.all([fetchPromise, timeoutPromise]);

    const student_name = student.name;

    let suggestions = [
      `I can assist you with more information about ${student_name}. Please click on a button below...`,
      `Student's job description`,
      `Summary of all reports submitted`,
      `Summary of all feedback given`,
      `Average of scores given`
    ];

    setStudentAccountId(student.accountId);
    setIsAriaChatVisible(true);
    setAriaSuggestions(suggestions);
    setCustomDesc(``);
    setAskAriaLoadingIndex(null);
  };

  useLayoutEffect(() => {
    if (!isAriaChatVisible || !isDesktop || !ariaChatRef.current) return;

    const rect = ariaChatRef.current.getBoundingClientRect();
    const chatWidth = rect.width;
    const chatHeight = rect.height;
    const padding = 20;

    let newX = window.innerWidth - chatWidth - padding;
    let newY = 100;

    newX = Math.max(padding, Math.min(window.innerWidth - chatWidth - padding, newX));
    newY = Math.max(padding, Math.min(window.innerHeight - chatHeight - padding, newY));

    setAriaChatPos({ x: newX, y: newY });
  }, [isAriaChatVisible, isDesktop]);

  useEffect(() => {
    function handleResize() {
      if (!ariaChatRef.current) return;
      const rect = ariaChatRef.current.getBoundingClientRect();
      const chatWidth = rect.width || 400;
      const chatHeight = rect.height || 500;
      const padding = 20;

      let newX = ariaChatPos.x;
      let newY = ariaChatPos.y;

      if (newX + chatWidth + padding > window.innerWidth) {
        newX = window.innerWidth - chatWidth - padding;
      }
      if (newX < padding) newX = padding;
      if (newY + chatHeight + padding > window.innerHeight) {
        newY = window.innerHeight - chatHeight - padding;
      }
      if (newY < padding) newY = padding;

      if (newX !== ariaChatPos.x || newY !== ariaChatPos.y) {
        setAriaChatPos({ x: newX, y: newY });
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [ariaChatPos, ariaChatRef]);

  const tooltipContent = (i: number, row: any, report: any, title: string) => {
    const period = title === reportTypeOptions[0].name ? `- ${i + 1}` : `Month ${i + 1}`;
    return (
      <div className="submission-tooltip">
        <div className="tooltip-title">
          {title} Report {period}
        </div>
        <div className="tooltip-row">
          <span className="tooltip-label">Deadline</span>
          <span className="tooltip-separator">:</span>
          <span className="tooltip-value">
            {getFormattedDate(report.deadline)}
          </span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-label">Submitted</span>
          <span className="tooltip-separator">:</span>
          <div className="tooltip-value">
            {report.submittedDate ? (
              <>
                {report.status !== "overdue" && (
                  <div className="on-time">On Time</div>
                )}
                <div>{getFormattedDate(report.submittedDate)}</div>
              </>
            ) : (
              "-"
            )}
          </div>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-label">Sentiment</span>
          <span className="tooltip-separator">:</span>
          {title == "Workplace Learning" ? (
            <span
              className={`tooltip-value ${getSentimentColor(
                row.sentiment[i] ?? ""
              )}`}
            >
              {row.sentiment[i] ? row.sentiment[i] : "-"}
            </span>
          ) : (
            ""
          )}
        </div>
        <div className="tooltip-row">
          <span className="tooltip-label">Feedback Given</span>
          <span className="tooltip-separator">:</span>
          <span className="tooltip-value">
            {getFormattedDate(report.feedbackDate)}
          </span>
        </div>
        {report._id && (
          <>
            {report.type === SUBMISSION_WL && (
              <a
                className="view-report-button"
                href={`${ROUTES.PORTAL_MY_GROUP_REPORT_WL}/${report._id}`}
              >
                View Report
              </a>
            )}
            {report.type === SUBMISSION_PD && (
              <a
                className="view-report-button"
                href={`${ROUTES.PORTAL_MY_GROUP_REPORT_PD}/${report._id}`}
              >
                View Report
              </a>
            )}
          </>
        )}
      </div>
    );
  };

  const getReportUrl = (report: any) => {
    let reportPath = report._id
      ? report.type === SUBMISSION_WL
        ? ROUTES.PORTAL_MY_GROUP_REPORT_WL
        : report.type === SUBMISSION_PD
        ? ROUTES.PORTAL_MY_GROUP_REPORT_PD
        : null
      : null;
    if (reportPath) {
      return `${reportPath}/${report._id}`;
    } else {
      return null;
    }
  };

  const getReportStatus = (report: any) => {
    if (report.submittedDate) {
      if (report.submittedDate > report.deadline) {
        return "overdue";
      }
      return "submitted";
    }

    if (report.statusDate === "past") {
      return "pending";
    } else if (report.statusDate === "future") {
      return "empty";
    } else {
      return "pending";
    }
  };

  const getCohortGroupByProgramme = () => {
    const cohortGroupByProgramme = {};
    if (!props.user || !props.user.groupHash) {
      return cohortGroupByProgramme;
    }
    Object.keys(props.user.groupHash).forEach((group) => {
      var programme = props.user.groupHash[group]?.cohort?.programme;
      if (!programme) {
        return;
      }
      const cohort = { ...props.user.groupHash[group].cohort };
      delete cohort.programme;
      if (cohortGroupByProgramme[programme._id]) {
        if (!cohort) {
          return;
        }
        if (
          !cohortGroupByProgramme[programme._id].cohorts.some(
            (c) => c._id === cohort._id
          )
        ) {
          cohortGroupByProgramme[programme._id].cohorts.push(cohort);
        }
      } else {
        cohortGroupByProgramme[programme._id] = {
          name: programme.name,
          cohorts: cohort ? [cohort] : [],
        };
      }
    });
    return cohortGroupByProgramme;
  };

  const getProgrammeOptions = (cohortGroupByProgramme: object) => {
    return Object.keys(cohortGroupByProgramme ?? {}).map((programmeId) => ({
      label: cohortGroupByProgramme[programmeId].name,
      value: programmeId,
    }));
  };

  const getCohortOptions = (programmeId: string) => {
    return cohortGroupByProgramme[programmeId]?.cohorts.map((cohort) => ({
      label: cohort.name,
      value: cohort._id,
      pdMode: cohort.pdMode,
    }));
  };

  const getFormattedDate = (date) => {
    if (!date) return "N/A";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getSentimentEmoji = (type: string) => {
    if (type === "pending") {
      return "";
    }

    switch (type) {
      case "Positive":
        return "/assets/images/icon/icon-reaction-good.svg";
      case "Neutral":
        return "/assets/images/icon/icon-reaction-neutral.svg";
      case "Negative":
        return "/assets/images/icon/icon-reaction-mad.svg";
      default:
        return "";
    }
  };

  const getSentimentColor = (sentiment: string | null) => {
    switch (sentiment) {
      case "Positive":
        return "positive";
      case "Neutral":
        return "neutral";
      case "Negative":
        return "negative";
      default:
        return "empty";
    }
  };

  const handleStudentNameClick = (student: any) => {
    const programmeName = programmeOptions.find(opt => opt.value === programmeId)?.label || "Unknown Programme";

    return ModulePopup.showAlertInfo({
      title: `${student.name}'s Profile`,
      description:
        <div className="student-profile-popup">
          <ol className="contact-detail-list">
            <li>
              <div>Email</div>
              <div>{student.email}</div>
            </li>
            <li>
              <div>Programme</div>
              <div>{showLabelInfo(programmeName)}</div>
            </li>
            <li>
              <div>Job Title</div>
              <div>{showLabelInfo(student.jobTitle)}</div>
            </li>
            <li>
              <div>Job Description</div>
              <div>
                <NewLineToBreak value={student?.jobDescription ?? "-"} />
              </div>
            </li>
        </ol>
      </div>
    })
  }

  return (
    <main
      className="content-generic content-generic--fluid"
      data-comp="PageDashboardSPV"
    >
      <div className="d-flex gap-2 justify-content-between align-items-center flex-wrap">
        <h1 className="fs-title">{PAGE_TITLE}</h1>
      </div>

      {(programmeOptions.length > 1 || cohortOptions.length > 1) && <div>
        <p>Please select a Programme and a Cohort from below</p>
        <div className="row gap-3 row-cols-lg-4 mt-2 mb-4 align-items-center mx-0">
          <div className="col-md-5 m-0 px-0 filter-group-control">
            <FGDropdownApi
              placeholder="Select Programme..."
              className="m-0 compact-dropdown"
              options={programmeOptions}
              value={programmeId}
              defaultValue={programmeId}
              disabled={loading}
              onChange={(e: any) => {
                location.state = null;
                setProgrammeId(e.target.value);
                setCohortId("0");
              }}
            />
          </div>
          <div className="col-md-5 m-0 px-0 filter-group-control">
            <FGDropdownApi
              placeholder="Select Cohort..."
              className="m-0 compact-dropdown"
              options={cohortOptions}
              value={cohortId}
              defaultValue={cohortId}
              disabled={loading}
              onChange={(e: any) => {
                location.state = null;
                setCohortId(e.target.value);
              }}
            />
          </div>

          <button
            type="button"
            name="submit"
            className="btn btn-secondary compact-btn col-md-4 m-0"
            onClick={() => currentPage !== 1 ? setCurrentPage(1) : handleSubmitChangeProgrammeCohort()}
            disabled={loading}
          >
            Submit
          </button>
        </div>
      </div>}

      <div className="reg-tabs">
        {(reportTypes.length > 0) && <ol className="reg-tabs__nav">
          {reportTypes
            .map((type, index) => (
              <li key={`report-type-${index}`}>
                <button
                  type="button"
                  className={classNames([
                    "reg-tabs__nav-btn",
                    { "reg-tabs__nav-btn--active": reportType === type.name },
                  ])}
                  onClick={(e) => {
                    if (loading) return;
                    e.preventDefault();
                    setReportType(type.name);
                  }}
                >
                  {type.label}
                  {type.tooltip !== "" && <span
                    className="ms-2"
                    onMouseEnter={(e) =>
                      handleMouseEnter(
                        e,
                        <p className="m-0">{type.tooltip}</p>,
                        false
                      )
                    }
                    onMouseLeave={handleMouseLeave}
                  >
                    <FaRegQuestionCircle />
                  </span>}
                </button>
              </li>
            ))}
        </ol>}

        {loading && (
          <div className="w-100 d-flex justify-content-center mt-2">
            <div className="loader"></div>
          </div>
        )}

        {
          alertText !== ""
            ? (!loading &&
              <div className="alert alert-info mt-4 text-center" role="alert">
              {alertText}
              </div>
            )
            : (!loading && (
              reportType === SUBMISSION_PD ||
              reportType === SUBMISSION_WL
            ) &&
              <>
                <div className="d-flex align-items-center flex-wrap mt-3">
                  <div className="spvd__legend">
                    <span className="legend-title">Legend</span>
                    <div className="d-flex align-items-center">
                      <span className="title">Submission:</span>
                      <span className="pending">
                        <span></span> Pending
                      </span>
                      <span className="submitted">
                        <span></span> On Time
                      </span>
                      <span className="overdue">
                        <span></span> Late
                      </span>
                      <span className="feedback-given">
                        <label
                          htmlFor="custom-checkbox"
                          className="custom-checkbox"
                        ></label>
                        Feedback Given
                      </span>
                    </div>
                    {reportType === SUBMISSION_WL && <div className="d-flex align-items-center">
                      <span className="title">Sentiment:</span>
                      <span className="positive">
                        <span></span> Positive
                      </span>
                      <span className="neutral">
                          <span></span> Neutral
                      </span>
                      <span className="negative">
                        <span></span> Negative
                      </span>
                    </div>}
                  </div>
                </div>
                <div className={`pd-tabs__content pt-0`}>
                  <div className="pd-tabs__content__table">
                    <table className="spvd__table">
                      <colgroup>
                        <col className="colg-programme"></col>
                        <col className="colg-cohort"></col>
                        <col className="colg-student"></col>
                        <col className="colg-job-title"></col>
                        <col className="colg-submission-progress"></col>
                        {reportType === reportTypeOptions[0].name && <>
                          <col className="colg-sentiment"></col>
                          <col className="colg-actions"></col>
                        </>}
                      </colgroup>
                      <thead>
                          <tr>
                            <th
                              className="col-programme"
                              onClick={() => toggleSort("programme")}
                              tabIndex={0}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="sorted-item">
                                Programme
                                <span
                                  className="sort-icon"
                                  role="button"
                                  style={{ color: sort.field === "programme" ? '#2ec4b6' : '#b0b0b0' }}
                                >
                                  {sort.field === "programme" ? <> {sort.direction === "asc" ? <FaArrowDown /> : <FaArrowUp />} </> : <FaArrowUp /> }
                                </span>
                              </div>
                            </th>
                            <th
                              className="col-cohort"
                              onClick={() => toggleSort("cohort")}
                              tabIndex={0}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="sorted-item">
                                Cohort
                                <span
                                  className="sort-icon"
                                  role="button"
                                  style={{ color: sort.field === "cohort" ? '#2ec4b6' : '#b0b0b0' }}
                                >
                                  {sort.field === "cohort" ? <> {sort.direction === "asc" ? <FaArrowDown /> : <FaArrowUp />} </> : <FaArrowUp /> }
                                </span>
                              </div>
                            </th>
                            <th
                              className="col-student"
                              onClick={() => toggleSort("student")}
                              tabIndex={0}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="sorted-item">
                                Student's Name and Email
                                <span
                                  className="sort-icon"
                                  role="button"
                                  style={{ color: sort.field === "student" ? '#2ec4b6' : '#b0b0b0' }}
                                >
                                  {sort.field === "student" ? <> {sort.direction === "asc" ? <FaArrowDown /> : <FaArrowUp />} </> : <FaArrowUp />}
                                </span>
                              </div>
                            </th>
                            <th className="col-job-title">Job Title / Company</th>
                            <th className={`col-submission-progress ${reportType === SUBMISSION_WL ? 'wl' : 'pd'}`}>Submission Progress</th>
                          {reportType === reportTypeOptions[0].name && (
                            <>
                              <th className="col-sentiment">Sentiment</th>
                              <th className="col-actions"></th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {studentRows.map((row, index) => (
                          <tr key={index}>
                            <td>
                              {showLabelInfo((row.programmeName) || "-")}
                            </td>
                            <td>
                              {showLabelInfo((row.cohortName) || "-")} <br />
                              <span>({moment(row.cohortStartDate).format("MMM YYYY")})</span>
                            </td>
                            <td className="col-cont-student">
                              <div className="tc-student">
                                <div className="tcs__name">
                                  <a
                                    href="#"
                                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                    onClick={e => {
                                      e.preventDefault();
                                      handleStudentNameClick(row);
                                    }}
                                  >
                                    {row.name}
                                  </a>
                                </div>
                                <div className="tcs__email" title={row.email}>
                                  ({row.email})
                                </div>
                              </div>
                            </td>
                            <td>
                              {row.jobTitle} <br />{" "}
                              {row.organizationName}
                            </td>
                            <td className="col-cont-submission-progress">
                              <div className={`submission-progress ${reportType === SUBMISSION_WL ? 'wl' : 'pd'}`}>
                                {row.reports.map((report: any, i: number) => (
                                  <a
                                    key={i}
                                    className={`submission-item submission-item-${report.statusDate} ${report.status}`}
                                    onMouseEnter={(e) =>
                                      report.status !== "empty" &&
                                      handleMouseEnter(
                                        e,
                                        tooltipContent(i, row, report, reportType)
                                      )
                                    }
                                    onMouseLeave={handleMouseLeave}
                                    href={viewport.isMobile ? "#" : getReportUrl(report)}
                                  >
                                    <div className="submission-item__content">
                                      {report.feedbackDate ? (
                                        <span className="check-icon">✓</span>
                                      ) : (
                                        <span>{i + 1}</span>
                                      )}
                                    </div>
                                  </a>
                                ))}
                              </div>
                            </td>
                            {reportType === reportTypeOptions[0].name && (
                              <>
                                <td className="col-cont-sentiment">
                                  <div className="sentiment">
                                    {row.sentiment.map((type, i) => (
                                      <a
                                        key={i}
                                        className={`sentiment-icon ${type}`}
                                        onMouseEnter={(e) =>
                                          type !== "empty" &&
                                          handleMouseEnter(
                                            e,
                                            tooltipContent(
                                              i,
                                              row,
                                              row.reports[i],
                                              "Workplace Learning"
                                            )
                                          )
                                        }
                                        href={
                                          type !== "empty" && row.reports[i]?.status !== "empty" ?
                                          (viewport.isMobile ? "#" : getReportUrl(row.reports[i])) :
                                          null
                                        }
                                        onMouseLeave={handleMouseLeave}
                                      >
                                        {/* {getSentimentEmoji(type) ? (
                                          <img
                                            src={getSentimentEmoji(type)}
                                            className="sentiment-icon__img"
                                            alt="icon"
                                          />
                                        ) : (
                                          ""
                                        )} */}
                                      </a>
                                    ))}
                                  </div>
                                </td>
                                <td className="col-cont-actions">
                                  {user.role === ROLE.SUPERVISOR && <button
                                    className="ask-aria"
                                    onClick={handleARIAClick(index, row)}
                                    disabled={!row.reports.some(report => !!report._id) || askAriaLoadingIndex === index}
                                    title={!row.reports.some(report => !!report._id) ? "No report submitted yet" : ""}
                                  >
                                    {askAriaLoadingIndex === index ? (
                                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                      "Ask ARIA"
                                    )}
                                  </button>}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {totalCount > 0 && (
                    <div className="pagination-container">
                      <div className="pagination-info">
                        {/* Showing {Math.min((currentPage - 1) * (itemsPerPage === 0 ? totalCount : itemsPerPage) + 1, totalCount)} to {Math.min(currentPage * (itemsPerPage === 0 ? totalCount : itemsPerPage), totalCount)} of {totalCount} */}
                      </div>
                      <div className="pagination-controls">
                        <div className="pagination-controls__show">
                          {/* <label>Show:</label> */}
                          <select
                            value={itemsPerPage}
                            onChange={(e) => {
                              setItemsPerPage(parseInt(e.target.value));
                              setCurrentPage(1);
                            }}
                            className="pagination-controls__select"
                          >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={0}>All</option>
                          </select>
                        </div>
                        {itemsPerPage > 0 && (
                          <div className="pagination-controls__pages">
                            {/* Page Number Pagination */}
                            {(() => {
                              const totalPages = Math.ceil(totalCount / itemsPerPage);
                              const pageNumbers = [];
                              const maxPageButtons = 3;
                              let startPage = Math.max(1, currentPage - 1);
                              let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
                              if (endPage - startPage < maxPageButtons - 1) {
                                startPage = Math.max(1, endPage - maxPageButtons + 1);
                              }
                              // Always show navigation buttons, use disabled if not available
                              pageNumbers.push(
                                <button
                                  key="first"
                                  onClick={() => currentPage > 1 && setCurrentPage(1)}
                                  className="pagination-controls__btn"
                                  disabled={currentPage === 1}
                                >
                                  <FiChevronsLeft />
                                </button>
                              );
                              pageNumbers.push(
                                <button
                                  key="prev"
                                  onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                                  className="pagination-controls__btn"
                                  disabled={currentPage === 1}
                                >
                                  <FiChevronLeft />
                                </button>
                              );
                              if (startPage > 1) {
                                pageNumbers.push(<span key="start-ellipsis" className="pagination-controls__ellipsis">...</span>);
                              }
                              for (let i = startPage; i <= endPage; i++) {
                                pageNumbers.push(
                                  <button
                                    key={i}
                                    onClick={() => setCurrentPage(i)}
                                    className={`pagination-controls__btn${i === currentPage ? ' pagination-controls__btn--active' : ''}`}
                                  >
                                    {i}
                                  </button>
                                );
                              }
                              if (endPage < totalPages) {
                                pageNumbers.push(<span key="end-ellipsis" className="pagination-controls__ellipsis">...</span>);
                              }
                              // Always show navigation buttons, use disabled if not available
                              pageNumbers.push(
                                <button
                                  key="next"
                                  onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                                  className="pagination-controls__btn"
                                  disabled={currentPage === totalPages}
                                >
                                  <FiChevronRight />
                                </button>
                              );
                              pageNumbers.push(
                                <button
                                  key="last"
                                  onClick={() => currentPage < totalPages && setCurrentPage(totalPages)}
                                  className="pagination-controls__btn"
                                  disabled={currentPage === totalPages}
                                >
                                  <FiChevronsRight />
                                </button>
                              );
                              return pageNumbers;
                            })()}
                          </div>
                        )}
                        Showing {Math.min((currentPage - 1) * (itemsPerPage === 0 ? totalCount : itemsPerPage) + 1, totalCount)} to {Math.min(currentPage * (itemsPerPage === 0 ? totalCount : itemsPerPage), totalCount)} of {totalCount}
                      </div>
                    </div>
                  )}

                  {/* ARIA */}
                  <div
                    className={`sit__dashboard__aria__container${isDragging ? " is-dragging" : ""}`}
                    style={{
                      display: isAriaChatVisible ? "block" : "none",
                      position: isDesktop || !ENABLE_CHAT_DRAG_MODE ? "fixed" : "static",
                      top: isDesktop && ENABLE_CHAT_DRAG_MODE ? ariaChatPos.y : undefined,
                      left: isDesktop && ENABLE_CHAT_DRAG_MODE ? ariaChatPos.x : undefined,
                      zIndex: 1050,
                      boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                      }}
                    ref={ariaChatRef}
                  >
                  <ARIAChat
                      custom_title=""
                      suggestions={ariaSuggestions}
                      stay_expanded={true}
                      stay_expanded_callback={() => setIsAriaChatVisible(false)}
                      custom_desc={customDesc}
                      is_api={true}
                      is_supervisor={true}
                      studentId={studentAccountId}
                      cohortId={cohortIdForAria}
                      student_reports_feedbacks={askAriaStudentReportsFeedbacks}
                      reports={currentStudentReports}
                      show_clear_chat_button={true}
                      onHeaderMouseDown={startAriaDrag}
                      isDragging={isDragging && ENABLE_CHAT_DRAG_MODE}
                      enable_drag={ENABLE_CHAT_DRAG_MODE}
                    />
                  </div>
                </div>
              </>
            )}
      </div>
      <Tooltip
        content={tooltip.content}
        position={tooltip.position}
        closeButton={tooltip.closeButton}
        onClose={handleTooltipMouseLeave}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      />
    </main>
  );
}
